import axios from 'axios';
import BASE_URL from '../../config';

// Create axios instance with base URL for tickets API
const API = axios.create({
  baseURL: `${BASE_URL}/api/`,
});

// Add authorization header to all requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== TICKET ENDPOINTS ====================

/**
 * Get all tickets with optional filters
 * @param {Object} filters - { status, priority, assigned_to_me }
 * @returns {Promise} Array of tickets
 */
export const getTickets = async (filters = {}) => {
  const params = {};

  if (filters.status && filters.status !== 'all') {
    params.status = filters.status.toUpperCase();
  }

  if (filters.priority && filters.priority !== 'all') {
    params.priority = filters.priority.toUpperCase();
  }

  if (filters.issue_type && filters.issue_type !== 'all') {
    params.issue_type = filters.issue_type.toUpperCase();
  }

  if (filters.assigned_to_me) {
    params.assigned_to_me = 'true';
  }

  const response = await API.get('tickets/', { params });
  return response.data;
};

/**
 * Get single ticket by ID
 * @param {number} id - Ticket ID
 * @returns {Promise} Ticket object with details
 */
export const getTicketById = async (id) => {
  const response = await API.get(`tickets/${id}/`);
  return response.data;
};

/**
 * Create a new ticket
 * @param {Object} ticketData - { title, description, priority }
 * @returns {Promise} Created ticket object
 */
export const createTicket = async (ticketData) => {
  const response = await API.post('tickets/create/', ticketData);
  return response.data;
};

/**
 * Update an existing ticket
 * @param {number} id - Ticket ID
 * @param {Object} ticketData - Fields to update
 * @returns {Promise} Updated ticket object
 */
export const updateTicket = async (id, ticketData) => {
  const response = await API.patch(`tickets/${id}/update/`, ticketData);
  return response.data;
};

/**
 * Delete a ticket
 * @param {number} id - Ticket ID
 * @returns {Promise} Success message
 */
export const deleteTicket = async (id) => {
  const response = await API.delete(`tickets/${id}/delete/`);
  return response.data;
};

/**
 * Assign ticket to an admin user (admin only)
 * @param {number} id - Ticket ID
 * @param {number|null} userId - User ID to assign, or null to unassign
 * @returns {Promise} Updated ticket object
 */
export const assignTicket = async (id, userId) => {
  const response = await API.post(`tickets/${id}/assign/`, {
    assigned_to: userId
  });
  return response.data;
};

// ==================== COMMENT ENDPOINTS ====================

/**
 * Get all comments for a ticket
 * @param {number} ticketId - Ticket ID
 * @returns {Promise} Array of comments
 */
export const getComments = async (ticketId) => {
  const response = await API.get(`tickets/${ticketId}/comments/`);
  return response.data;
};

/**
 * Create a comment on a ticket
 * @param {number} ticketId - Ticket ID
 * @param {string} comment - Comment text
 * @returns {Promise} Created comment object
 */
export const createComment = async (ticketId, comment) => {
  const response = await API.post(`tickets/${ticketId}/comments/create/`, {
    comment
  });
  return response.data;
};

/**
 * Update a comment
 * @param {number} ticketId - Ticket ID
 * @param {number} commentId - Comment ID
 * @param {string} comment - Updated comment text
 * @returns {Promise} Updated comment object
 */
export const updateComment = async (ticketId, commentId, comment) => {
  const response = await API.patch(`tickets/${ticketId}/comments/${commentId}/update/`, {
    comment
  });
  return response.data;
};

/**
 * Delete a comment
 * @param {number} ticketId - Ticket ID
 * @param {number} commentId - Comment ID
 * @returns {Promise} Success message
 */
export const deleteComment = async (ticketId, commentId) => {
  const response = await API.delete(`tickets/${ticketId}/comments/${commentId}/delete/`);
  return response.data;
};

// ==================== ATTACHMENT ENDPOINTS ====================

/**
 * Get all attachments for a ticket
 * @param {number} ticketId - Ticket ID
 * @returns {Promise} Array of attachments
 */
export const getAttachments = async (ticketId) => {
  const response = await API.get(`tickets/${ticketId}/attachments/`);
  return response.data;
};

/**
 * Upload an attachment to a ticket
 * @param {number} ticketId - Ticket ID
 * @param {File} file - File object to upload
 * @returns {Promise} Created attachment object
 */
export const uploadAttachment = async (ticketId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await API.post(
    `tickets/${ticketId}/attachments/upload/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Delete an attachment
 * @param {number} ticketId - Ticket ID
 * @param {number} attachmentId - Attachment ID
 * @returns {Promise} Success message
 */
export const deleteAttachment = async (ticketId, attachmentId) => {
  const response = await API.delete(`tickets/${ticketId}/attachments/${attachmentId}/delete/`);
  return response.data;
};

// ==================== STATISTICS ENDPOINTS (Admin only) ====================

/**
 * Get ticket statistics (admin only)
 * @returns {Promise} Statistics object
 */
export const getStatistics = async () => {
  const response = await API.get('tickets/statistics/');
  return response.data;
};

/**
 * Get list of admin users for ticket assignment (admin only)
 * @returns {Promise} Array of admin users
 */
export const getAdminUsers = async () => {
  const response = await API.get('tickets/admin-users/');
  return response.data;
};

export default API;
